var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import styled from 'styled-components';
import { margin, variant } from 'styled-system';
import { theme } from '../../essentials/theme';
import { XCrossIcon } from '../../icons';
import { get } from '../../utils/themeGet';
import { getSemanticValue } from '../../utils/cssVariables';
import { Text } from '../Text/Text';
const TagText = styled(Text).attrs({ theme }).withConfig({ displayName: "TagText", componentId: "sc-9zb5us" }) `
    color: ${getSemanticValue('foreground-info-faded')};
    margin-left: 0.75rem;
    margin-right: ${props => (props.dismissible ? '0.25rem' : '0.75rem')};
    font-size: ${get('fontSizes.1')};
    font-weight: ${get('fontWeights.semibold')};
    cursor: default;
    transition: color 125ms ease;
`;
const DismissIcon = styled(XCrossIcon).attrs({ size: 18 }).withConfig({ displayName: "DismissIcon", componentId: "sc-1mzqzz3" }) `
    margin-right: 0.5rem;
    transition: color 125ms ease;

    &:hover {
        cursor: pointer;
    }
`;
const tagVariant = variant({
    variants: {
        default: {
            backgroundColor: getSemanticValue('background-element-info-default'),
            borderColor: getSemanticValue('border-info-default'),
            [`> ${TagText}`]: {
                color: getSemanticValue('foreground-info-default')
            },
            [`> ${DismissIcon}`]: {
                color: getSemanticValue('foreground-info-default')
            },
            '&:hover': {
                backgroundColor: getSemanticValue('background-element-info-emphasized'),
                borderColor: getSemanticValue('border-info-default'),
                [`> ${TagText}`]: {
                    color: getSemanticValue('foreground-on-background-info')
                },
                [`> ${DismissIcon}`]: {
                    color: getSemanticValue('foreground-on-background-info')
                }
            }
        },
        disabled: {
            borderColor: getSemanticValue('border-disabled'),
            [`> ${TagText}`]: {
                color: getSemanticValue('foreground-disabled')
            },
            [`> ${DismissIcon}`]: {
                color: getSemanticValue('foreground-disabled')
            }
        },
        error: {
            backgroundColor: getSemanticValue('background-surface-danger-default'),
            borderColor: getSemanticValue('border-danger-default'),
            [`> ${TagText}`]: {
                color: getSemanticValue('foreground-danger-default')
            },
            [`> ${DismissIcon}`]: {
                color: getSemanticValue('foreground-danger-default')
            },
            '&:hover': {
                backgroundColor: getSemanticValue('background-surface-danger-emphasized'),
                borderColor: getSemanticValue('border-danger-default'),
                [`> ${TagText}`]: {
                    color: getSemanticValue('foreground-on-background-danger')
                },
                [`> ${DismissIcon}`]: {
                    color: getSemanticValue('foreground-on-background-danger')
                }
            }
        }
    }
});
const TagWrapper = styled.div.attrs({ theme }).withConfig({ displayName: "TagWrapper", componentId: "sc-1yarfvi" }) `
    box-sizing: border-box;
    border: solid 0.0625rem;
    display: inline-flex;
    align-items: center;
    border-radius: 2rem;
    height: 2rem;
    margin-right: 0.25rem;
    margin-bottom: 0.375rem;
    padding: 0.375rem 0;
    transition: background-color 125ms ease;

    ${margin}
    ${tagVariant}
`;
const Tag = (_a) => {
    var { children, onDismiss, dismissible = true, variant: variantValue = 'default' } = _a, rest = __rest(_a, ["children", "onDismiss", "dismissible", "variant"]);
    return (React.createElement(TagWrapper, Object.assign({ variant: variantValue }, rest),
        React.createElement(TagText, { dismissible: dismissible }, children),
        dismissible && (React.createElement(DismissIcon, { "data-testid": "dismiss-icon", color: getSemanticValue('foreground-info-faded'), onClick: onDismiss }))));
};
export { Tag };
